import React from 'react';
import './App.css';
import { Anchor, Box, Main, Text } from 'grommet';
import {
    Navigate,
    Outlet,
    redirect,
    useLoaderData,
    useMatches,
    useNavigate,
} from 'react-router-dom';
import {
    SearchBar,
    clearUserData,
    hasToken,
    hasUserId,
    UserService,
    AppConstants,
    userIsCoach,
    userIsResumeReviewer,
    userCanReviewResumes,
    userIsAdmin,
    userIsTaskmaster,
    userIsWelcomeCallCoach,
    userCanViewWelcomeCalls,
} from '@enrich/shared-lib';

const NAV_LINKS = {
    coach: [
        { href: '/dashboard', label: 'Dashboard' },
        { href: '/search', label: 'Student Search' },
        { href: '/applicants', label: 'Applicants' },
        { href: '/resumes', label: 'Resumes' },
        { href: '/jobs', label: 'Jobs' },
        { href: '/pipelines', label: 'Pipelines' },
        { href: '/companies', label: 'Companies' },
        { href: '/schools', label: 'Schools' },
        { href: '/lists', label: 'Lists' },
        { href: '/referrals', label: 'Referrals' },
        { href: '/invite', label: 'Invite' },
    ],
    admin: [
        { href: '/dashboard', label: 'Dashboard' },
        { href: '/search', label: 'Student Search' },
        { href: '/applicants', label: 'Applicants' },
        { href: '/resumes', label: 'Resumes' },
        { href: '/jobs', label: 'Jobs' },
        { href: '/pipelines', label: 'Pipelines' },
        { href: '/companies', label: 'Companies' },
        { href: '/schools', label: 'Schools' },
        { href: '/lists', label: 'Lists' },
        { href: '/referrals', label: 'Referrals' },
        { href: '/internal-review', label: 'Internal' },
        { href: '/invite', label: 'Invite' },
    ],
    taskmaster: [
        { href: '/tasks', label: 'Tasks' },
        { href: '/search', label: 'Student Search' },
        { href: '/jobs', label: 'Jobs' },
        { href: '/pipelines', label: 'Pipelines' },
        { href: '/companies', label: 'Companies' },
    ],
    // ENG-324: rendered for anyone `userCanReviewResumes` allows — the resume
    // reviewer role and the welcome call coach. Kept as its own block so the
    // permission has exactly one source of truth and neither role can end up
    // with a duplicate Resumes link.
    resumeReview: [{ href: '/resumes', label: 'Resumes' }],
    // ENG-367: same shape for welcome calls — rendered for anyone
    // `userCanViewWelcomeCalls` allows, the welcome call coach and the admin.
    // The admin block above carries no /welcome-calls entry, so neither role
    // ends up with a duplicate link.
    welcomeCallCoach: [{ href: '/welcome-calls', label: 'Welcome Calls' }],
    // ENG-391: job matches for the welcome call coach only (exact role match).
    // Coach, admin and taskmaster blocks carry their own Jobs entry, so no
    // role ends up with a duplicate link.
    welcomeCallCoachJobs: [{ href: '/jobs', label: 'Jobs' }],
};

const NavLinks = ({ links }) => (
    <Box direction="row" gap="medium">
        {links.map(({ href, label }) => (
            <Anchor key={href} href={href} label={label} />
        ))}
    </Box>
);

const AppHeader = () => (
    <Box
        align="center"
        background="enrich-blue"
        direction="row"
        justify="between"
        pad={{ top: 'large', bottom: 'medium', horizontal: 'large' }}>
        <Text>Enrich Coaching Platform</Text>
        <SearchBar />
    </Box>
);

const AppNav = ({ user }) => {
    const navigate = useNavigate();
    const isCoach = user?.role?.id === 3;
    const canReviewResumes = userCanReviewResumes(user);
    const isAdmin = userIsAdmin(user);
    const isTaskmaster = userIsTaskmaster(user);
    const canViewWelcomeCalls = userCanViewWelcomeCalls(user);
    const isWelcomeCallCoach = userIsWelcomeCallCoach(user);

    const logout = () => {
        clearUserData();
        navigate('/login');
    };

    return (
        <Box
            background="enrich-grey"
            direction="row"
            gap="medium"
            pad={{ vertical: 'small', horizontal: 'medium' }}>
            {isCoach && <NavLinks links={NAV_LINKS.coach} />}
            {isAdmin && <NavLinks links={NAV_LINKS.admin} />}
            {isTaskmaster && <NavLinks links={NAV_LINKS.taskmaster} />}
            {canViewWelcomeCalls && (
                <NavLinks links={NAV_LINKS.welcomeCallCoach} />
            )}
            {isWelcomeCallCoach && (
                <NavLinks links={NAV_LINKS.welcomeCallCoachJobs} />
            )}
            {canReviewResumes && <NavLinks links={NAV_LINKS.resumeReview} />}
            <Anchor default onClick={logout} label="Log out" />
        </Box>
    );
};

// A welcome call coach may only open a page whose route sets
// handle: { welcomeCallCoach: true }. Every other page, including new ones,
// sends them home.
const useWelcomeCallCoachIsBlocked = (user) => {
    const page = useMatches().at(-1);
    if (!userIsWelcomeCallCoach(user)) return false;
    return !page?.handle?.welcomeCallCoach;
};

export const App = () => {
    const user = useLoaderData();
    const isBlocked = useWelcomeCallCoachIsBlocked(user);

    return (
        <Main>
            <AppHeader />
            <AppNav user={user} />
            {isBlocked ? (
                <Navigate to="/welcome-calls" replace />
            ) : (
                <Outlet context={user} />
            )}
        </Main>
    );
};

export async function loader({ request }) {
    if (!hasToken() || !hasUserId()) {
        clearUserData();
        return redirect('/login');
    }

    const userId = localStorage.getItem(AppConstants.cacheKeys.userId);
    const user = await new UserService().getUser(userId);

    if (userIsCoach(user))
        localStorage.setItem(AppConstants.cacheKeys.coachId, user.coach?.id);

    if (userIsWelcomeCallCoach(user) && user.welcomeCallCoach?.id)
        localStorage.setItem(
            AppConstants.cacheKeys.welcomeCallCoachId,
            user.welcomeCallCoach.id,
        );

    if (userIsWelcomeCallCoach(user) && !user.tempPasswordChanged)
        return redirect('/change-password');

    const { pathname } = new URL(request.url);

    if (pathname === '/') {
        if (userIsWelcomeCallCoach(user)) return redirect('/welcome-calls');
        if (userIsCoach(user) || userIsAdmin(user))
            return redirect('/dashboard');
        if (userIsResumeReviewer(user)) return redirect('/resumes');
        return redirect('/tasks');
    }

    return user;
}
